import { CloudStorageData, IAssetable, ImgStorageData } from '@dataclouder/ngx-cloud-storage';
import { AudioSpeed, ConversationType, TextEngines } from './conversation-enums';
import { IAIModel, EModelQuality } from '@dataclouder/ngx-core';
import { EDoActionType, SystemPromptType } from '../components/dc-agent-form/form-group.utils';
import { AudioEffectType } from '@dataclouder/ngx-vertex';

// This will be for future reference.
export interface Appearance {
  generalDescription: string; // Summary of the character's appearance
  physicalDescription: string; // height, build, hairColor, hairStyle, eyeColor, skinTone,
  outfit: string; // clothing style, currentOutfit
  objects: string;
  quirks: string; //distinctiveMarks
}

export interface CharacterCardData {
  name: string;
  description: string;
  // personality: string; not needed
  scenario?: string;
  first_mes?: string;
  creator_notes?: string;
  mes_example?: string;
  alternate_greetings?: string[];
  tags?: string[];
  system_prompt?: string;
  post_history_instructions?: string;
  character_version?: string;
  extensions?: Record<string, any>;
  picture_description?: string;
  appearance?: Appearance; // height, build, hairColor, hairStyle, eyeColor, skinTone, distinctiveMarks, clothing style, currentOutfit
}

export const characterCardStringDataDefinition = `
interface CharacterCardData {
  name: string;                      // Character's name
  description: string;               // General character description
  scenario: string;                  // The setting or context where the character exists
  first_mes: string;                 // First message the character will say
  mes_example: string;               // Example of character's typical message/dialogue
  alternate_greetings: string[];     // Array of alternative greeting messages
  tags: string[];                    // Array of descriptive tags
  appearance: string;                // Physical description including height, build, hair color/style, eye color, skin tone, distinctive marks, clothing style, current outfit
}`;

export interface ICharacterCardDC {
  spec?: 'chara_card_v2';
  spec_version?: '2_v_dc';
  data: CharacterCardData;
}

export interface ITTS {
  voice: string;
  secondaryVoice?: string;
  speed?: AudioSpeed;
  speedRate?: number;
}

export interface IAgentMetadata {
  isPublished: boolean;
  isPublic: any;
  authorId: string;
  authorEmail: string;
  createdAt: Date;
  updatedAt: Date;
  takenCount: number;
  level: number;
}

export interface VoiceTTS {
  voice: string;
  speedRate?: number;
  lang?: string; // lang if not provider already in voice.
  effect?: string; // platform effect
  model?: string; // if apply defult comes with voice id
  provider?: string; // if apply default is google.
  options?: Record<string, any>; // Check later for future options.
}

export interface IConversationSettings {
  modelQuality?: EModelQuality; // Model quality to use for this task default is fast. use platform default

  textEngine?: TextEngines;
  conversationType?: ConversationType;
  tts?: ITTS; // @deprecated use mainVoice and secondaryVoice
  autoStart?: boolean; // usally start with  characterCard.first_mes but if not set also can start with LLM response.
  messages?: ChatMessage[]; // Esto es calculado por el prompt builder tomando el agente.
  last_prompt?: string; // Esto es calculado por el prompt builder tomando el agente.
  repeatRecording?: boolean; // not sure where to put it, for now here
  model?: IAIModel; // i think this is duplicated. with UserChatSettings
  mainVoice?: VoiceTTS;
  secondaryVoice?: VoiceTTS;
}

export type IConversationSettingsDTO = Partial<IConversationSettings> & {
  returnJson?: boolean;
};

export interface IAgentResponseDTO {
  role: ChatRole;
  content: string | object;
  metadata: any;
}

export interface IAgentAssets {
  image?: ImgStorageData;
  motion?: CloudStorageData; // Assuming motion might not always be a simple image
  bannerImg?: ImgStorageData;
  stickers?: Array<ImgStorageData>;
}

export enum EntityWhat {
  Goal = 'goal',
  Bonus = 'bonus',
  NumMessages = 'numMessages',
  Time = 'time',
}

export const EntityWhatOptions = [
  { label: 'Goal', value: EntityWhat.Goal },
  { label: 'Bonus', value: EntityWhat.Bonus },
  { label: 'NumMessages', value: EntityWhat.NumMessages },
  { label: 'Time', value: EntityWhat.Time },
];

export enum EntityWhen {
  LowerThan = '<',
  LowerThanOrEqual = '<=',
  Equal = '=',
  GreaterThanOrEqual = '>=',
  GreaterThan = '>',
}

export const EntityWhenOptions = [
  { label: '<', value: EntityWhen.LowerThan },
  { label: '<=', value: EntityWhen.LowerThanOrEqual },
  { label: '=', value: EntityWhen.Equal },
  { label: '>=', value: EntityWhen.GreaterThanOrEqual },
  { label: '>', value: EntityWhen.GreaterThan },
];

export enum EntityThen {
  ChangePrompt = 'changePrompt',
}

export type DoAction = {
  actionType: EDoActionType;
  systemPromptType: SystemPromptType;
  prompt: string;
};

export interface IDynamicCondition {
  what: EntityWhat;
  when: EntityWhen;
  value: string | number;
  then: EntityThen;
  task: SimpleAgentTask;
  do?: DoAction[];
}

export interface IConversationFlow {
  goal: AITask;
  triggerTasks: Partial<Record<ConversationEvents, AITask>>;
  bonus: any;
  dynamicConditions: IDynamicCondition[];
}

export interface IAgentCard {
  version?: string;
  _id?: string;
  id?: string;
  title?: string; // will change to name because Google AgentCard
  description?: string; // Not used yet.
  lang?: string;
  assets?: IAssetable;
  image?: ImgStorageData; // @Deprecated but pro have this flag on some scnarios fix prod i need it for now.
  characterCard?: ICharacterCardDC;
  conversationSettings?: IConversationSettings; // have conversation settings in app like text engine
  metaApp?: IAgentMetadata;
  accounts?: Array<IAccounts>;
  conversationFlow?: IConversationFlow;
}

export interface IAgentCardMeta extends IAgentCard {
  taken: any;
}

export const EvalResultStringDefinition = `
interface SimpleEvalResult {
  score: number;                       // Score of the user's response 0 to 3
  feedback: string;                    // Feedback of the user's understanding of the conversation
}`;

export interface SimpleEvalResult {
  score: number;
  feedback: string;
}

export interface LanguageEvaluation {
  score: number;
  grammarFix: string;
  lexicalFix: string;
  sentenceFix: string;
}

export interface ISimpleAgent {
  systemPrompt: string; // converts into { role: 'system', content: systemPrompt }
  model: IAIModel;
}

export interface ISimpleTask {
  task: string; // converts into { role: 'user', content: task }
  expectedResponseType?: string; // instructions for format, TS interface of the results
  sources?: string[]; // additional data to be send.
}

export interface SimpleAgentTask {
  task: string; // Description of the User or App Task
  modelQuality?: EModelQuality; // Model quality to use for this task default is fast. use platform default
  expectedResponseType?: string; // if this, enable json mode to return the object.
  systemPrompt?: string; // If needed System prompt to use for this task
  model?: IAIModel; // Full control of the model to use for this task
}

// No system prompt, expectedResponseType is selected by app, other wise expect content text.
export type AITask = Pick<SimpleAgentTask, 'task' | 'model'>;

export interface AppAgentTask {
  agent: ISimpleAgent;
  task: ISimpleTask;
}

export enum EAccountsPlatform {
  Tiktok = 'tiktok',
  Instagram = 'instagram',
  Youtube = 'youtube',
}

export interface IAccounts {
  platform: EAccountsPlatform;
  user: string;
  email: string;
  authMethod: string;
  pass: string;
}

export type TranscriptionsWhisper = { text: string; task: string; language: string; words?: any[]; duration: number; segments?: any[] };

export class WordTimestamps {
  word: string;
  start: number;
  end: number;
  highlighted: boolean = false; // Initialize with false and use proper boolean type
}

export class MessageContent {
  audioUrl?: string; // url of the audio
  audioPromise?: any; // promise to play the audio
  isLoading?: boolean; // when the audio or something is loading
  transcription?: TranscriptionsWhisper; // original trasncription from  whisper
  transcriptionTimestamps?: WordTimestamps[]; // transformed transcription to timestamps and highlighted words
  text?: string; // text extraction no format
  content?: string; // Original content returned from LLM, but can have format, like html or markdown etc.
  voice?: string; // voice to use for tts, if not set, use default voice from conversation settings
  shouldPlayAudio?: boolean; // Control flag for parent orchestration
  messageId?: string; // Identifier for tracking in sequences
  tag?: string; // if for markdown tranform the html tag em, string , etc.
  imgUrl?: string; // url of the image for avatar dialog
  ssml?: string; // Text valid in Speech Synthesis Markup Language
  audioEffect?: AudioEffectType; // Defines the audio effect to apply ('reverb', 'echo', 'none')
}

export enum ChatRole {
  System = 'system',
  User = 'user',
  Assistant = 'assistant',
  AssistantHelper = 'assistantHelper',
}

export class ChatMessage extends MessageContent {
  role: ChatRole; // Assistant | User |  AssistantHelper returned from LLM but i agregate
  translation?: string;
  audioHtml?: HTMLAudioElement; // this is to get a reference to node so i can subscribe to events
  promisePlay?: any; // promise to play the audio
  stats?: any; // Not sure if i'm using this.
  multiMessages?: MessageContent[]; //
  evaluation?: Record<string, any>;
}

export class ConversationMessagesDTO {
  messages?: ChatMessage[];
  modelName?: string;
  provider?: string;
  type?: ConversationType;
}

export class ConversationDTO {
  id?: string;
  entityId?: string; // LearningExample or Scenario id
  type?: ConversationType;
  createdAt?: Date;
  messages: Array<ChatMessage>;
  transcription?: boolean;
  modelName?: string;
  provider?: string;
  returnJson?: boolean;
}

export interface ModelName {
  id: string;
  name: string;
}

export type AudioGenerated = { blobUrl: string; transcription: any };
export type TTSRequest = { text: string; voice: string; generateTranscription: boolean; speedRate: number; speed?: string; ssml?: string };

export interface BackgroundTask {
  task: SimpleAgentTask;
  event: ConversationEvents;
}

export enum ConversationEvents {
  OnUserMessage = 'onUserMessage',
  OnAssistantMessage = 'onAssistantMessage',
  OnGoalCompleted = 'onGoalCompleted',
  OnInitConversation = 'onInitConversation',
  OnEndConversation = 'onEndConversation',
}

// Define the structure for chat events emitted by DCChatComponent
export enum ChatEventType {
  WordClicked = 'wordClicked',
  // Add other potential event types here as needed
  // e.g., MessageSent = 'messageSent', SettingsChanged = 'settingsChanged', GoalCompleted = 'goalCompleted'
}

export interface ChatEvent<T = any> {
  type: ChatEventType;
  payload: T;
}

// Enum to define different types of conversation context for evaluation
export enum ContextType {
  CurrentMessageContent = 'MessageContent',
  LastAssistantMessage = 'LastAssistantMessage',
  LastUserMessage = 'LastUserMessage',
  LastDialog = 'LastDialog', // Last User + Last Assistant
  Last2Dialogs = 'Last2Dialogs', // Last 2 User + Last 2 Assistant
  LastUserAndPreviousAssistant = 'LastUserAndPreviousAssistant',
  AllConversation = 'AllConversation',
}
